import CheckIcon from "@mui/icons-material/Check";
import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useCrafts } from "../hooks/useCraftsQueries";
import { CRAFT_COLORS, craftUsingColor } from "../models/Craft";

type Props = {
	value: string;
	craftId: number | null;
	onChange: (color: string) => void;
};

type SwatchProps = {
	color: string;
	selected: boolean;
	usedBy?: string;
	onClick: () => void;
};

const Swatch = ({ color, selected, usedBy, onClick }: SwatchProps) => (
	<Tooltip title={usedBy ? `Used by ${usedBy}` : ""} placement="top" arrow>
		<Box component="span" sx={{ display: "block" }}>
			<Box
				component="button"
				type="button"
				aria-label={usedBy ? `${color}, used by ${usedBy}` : color}
				aria-pressed={selected}
				disabled={!!usedBy}
				onClick={onClick}
				sx={{
					width: "100%",
					aspectRatio: "1",
					borderRadius: "4px",
					border: "none",
					padding: 0,
					cursor: usedBy ? "not-allowed" : "pointer",
					background: color,
					opacity: usedBy ? 0.25 : 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "#fff",
					outline: selected ? "2px solid" : "none",
					outlineColor: "text.primary",
					outlineOffset: "2px",
					transition: "transform 120ms ease",
					"&:hover": { transform: usedBy ? "none" : "scale(1.08)" },
				}}
			>
				{selected && <CheckIcon sx={{ fontSize: 18 }} />}
			</Box>
		</Box>
	</Tooltip>
);

export const CraftColorPicker = ({ value, craftId, onChange }: Props) => {
	const { crafts } = useCrafts();
	const current = (value || "").trim();
	// A craft keeps its colour even when it is not in the palette, so saving never changes it.
	// It is remembered apart so it can be picked again after trying another one.
	const [original, setOriginal] = useState("");
	useEffect(() => {
		if (
			current !== "" &&
			!CRAFT_COLORS.some((c) => c.toLowerCase() === current.toLowerCase())
		)
			setOriginal(current);
	}, [current]);

	return (
		<Box>
			<Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
				Color
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "repeat(6, 1fr)", sm: "repeat(12, 1fr)" },
					gap: 1,
				}}
			>
				{CRAFT_COLORS.map((color) => (
					<Swatch
						key={color}
						color={color}
						selected={color.toLowerCase() === current.toLowerCase()}
						usedBy={craftUsingColor(crafts, color, craftId)?.CraftName}
						onClick={() => onChange(color)}
					/>
				))}
			</Box>
			{original && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
					<Box sx={{ width: 28 }}>
						<Swatch
							color={original}
							selected={original.toLowerCase() === current.toLowerCase()}
							onClick={() => onChange(original)}
						/>
					</Box>
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						Current color
					</Typography>
				</Box>
			)}
		</Box>
	);
};
